<template>
    <div class="py-12">
        <CrumbsBar
            :domain="reportedDomain.domain"
            :source="source"
            :ip="ip"
        ></CrumbsBar>

        <div>
            <StatsBarDetail
                :stats="{ pass: reports.total.pass, fail: reports.total.fail }"
                :stats-before="null"
            ></StatsBarDetail>
        </div>

        <StatsChart
            v-if="reports.total.pass > 0 || reports.total.fail > 0"
            :chart-data="reports.chartData"
            header="Daily email reports from this IP address"
        ></StatsChart>

        <div class="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div class="rounded-xl border border-gray-200 bg-white p-6">
                <h3 class="mb-6 font-medium text-gray-900">
                    Authentication Summary
                </h3>
                <div class="space-y-4">
                    <div
                        class="flex items-start gap-4 rounded-lg border border-green-100 bg-green-50 p-4"
                        :class="{
                            'border-yellow-100 bg-yellow-50':
                                passRates.dmarc < 75 && passRates.dmarc > 50,
                            'border-red-100 bg-red-50': passRates.dmarc <= 50,
                        }"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-green-500"
                            :class="{
                                'bg-yellow-500':
                                    passRates.dmarc < 75 &&
                                    passRates.dmarc > 50,
                                'bg-red-500': passRates.dmarc <= 50,
                            }"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-green-900">
                                {{ generateDmarcSummary(passRates).headline }}
                            </p>
                            <p class="text-sm text-green-800">
                                {{ generateDmarcSummary(passRates).summary }}
                            </p>
                        </div>
                    </div>
                    <div
                        class="flex items-start gap-4 rounded-lg border border-blue-100 bg-blue-50 p-4"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-blue-900">
                                Volume Analysis
                            </p>
                            <p class="text-sm text-blue-800">
                                Peak volume with
                                {{
                                    new Intl.NumberFormat().format(
                                        peakDay.pass + peakDay.fail,
                                    )
                                }}
                                emails processed.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-xl border border-gray-200 bg-white p-6">
                <h3 class="mb-6 font-medium text-gray-900">
                    {{ $t("vue.alignment_issues") }}
                </h3>
                <div class="space-y-4">
                    <div
                        v-if="firstInvalidSpf && firstInvalidSpf[1]"
                        class="flex items-start gap-4 rounded-lg border border-yellow-100 bg-yellow-50 p-4"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-500"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-yellow-900">
                                SPF Not Aligned
                            </p>
                            <p class="text-sm text-yellow-800">
                                {{
                                    new Intl.NumberFormat().format(
                                        firstInvalidSpf[1].spf_invalid,
                                    )
                                }}
                                emails using
                                {{ firstInvalidSpf[1].auth_results.spf.domain }}
                                for SPF verification.
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.spf_pass_rate")
                        }}</span
                        ><span
                            class="font-medium text-green-600"
                            :style="{
                                color: calculateColorByPercents(passRates.spf),
                            }"
                            >{{ passRates.spf }}%</span
                        >
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.dkim_pass_rate")
                        }}</span
                        ><span
                            class="font-medium text-green-600"
                            :style="{
                                color: calculateColorByPercents(passRates.dkim),
                            }"
                            >{{ passRates.dkim }}%</span
                        >
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.dmarc_pass_rate")
                        }}</span
                        ><span
                            class="font-medium text-green-600"
                            :style="{
                                color: calculateColorByPercents(
                                    passRates.dmarc,
                                ),
                            }"
                            >{{ passRates.dmarc }}%</span
                        >
                    </div>
                </div>
            </div>
        </div>

        <div
            class="mb-10 mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
            <div class="border-b border-gray-100 px-6 py-5">
                <div class="mb-4 flex items-center justify-between">
                    <div>
                        <h3 class="font-medium text-gray-900">
                            Email Authentication Results
                        </h3>
                        <p class="mt-1 text-sm text-gray-600">
                            SPF, DKIM, and DMARC verification status for all
                            emails from this IP. Click on domains or use the
                            action buttons to view detailed reports.
                        </p>
                    </div>
                    <div class="text-sm text-gray-600">
                        <span v-if="filters.length"
                            >Showing
                            <span class="font-medium text-gray-900">{{
                                Object.keys(filteredResults).length
                            }}</span>
                            of
                            <span class="font-medium text-gray-900">{{
                                Object.keys(reports.events).length
                            }}</span>
                            results</span
                        >
                        <span v-else
                            ><span class="font-medium text-gray-900">{{
                                Object.keys(reports.events).length
                            }}</span>
                            total results</span
                        >
                    </div>
                </div>
                <div class="flex flex-wrap items-center gap-3">
                    <div class="flex items-center gap-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-funnel h-4 w-4 text-gray-500"
                            aria-hidden="true"
                        >
                            <path
                                d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"
                            ></path>
                        </svg>
                        <span class="text-sm font-medium text-gray-700"
                            >{{ $t("vue.filters") }}:</span
                        >
                    </div>
                    <button
                        data-slot="button"
                        class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-background hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-red-200 px-3 text-sm font-medium text-red-700 outline-hidden transition-all hover:bg-red-50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                        :class="{
                            'bg-red-600 text-white hover:bg-red-700':
                                filters.includes('dmarc'),
                        }"
                        @click="addFilter('dmarc')"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-circle-x h-3 w-3"
                            aria-hidden="true"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="m15 9-6 6"></path>
                            <path d="m9 9 6 6"></path>
                        </svg>
                        DMARC Failed
                    </button>
                    <button
                        data-slot="button"
                        class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-background hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-yellow-200 px-3 text-sm font-medium text-yellow-700 outline-hidden transition-all hover:bg-yellow-50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                        :class="{
                            'bg-yellow-600 text-white! hover:bg-yellow-700':
                                filters.includes('spf'),
                        }"
                        @click="addFilter('spf')"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-circle-alert h-3 w-3"
                            aria-hidden="true"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" x2="12" y1="8" y2="12"></line>
                            <line x1="12" x2="12.01" y1="16" y2="16"></line>
                        </svg>
                        SPF Issues
                    </button>
                    <button
                        data-slot="button"
                        class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-background hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-orange-50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-orange-200 px-3 text-sm font-medium text-orange-700 outline-hidden transition-all focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                        :class="{
                            'bg-orange-600 text-white! hover:bg-orange-700':
                                filters.includes('dkim'),
                        }"
                        @click="addFilter('dkim')"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-circle-alert h-3 w-3"
                            aria-hidden="true"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" x2="12" y1="8" y2="12"></line>
                            <line x1="12" x2="12.01" y1="16" y2="16"></line>
                        </svg>
                        DKIM Issues
                    </button>
                    <button
                        v-if="filters.length"
                        data-slot="button"
                        class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:hover:bg-accent/50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium text-gray-600 outline-hidden transition-all hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                        @click="clearFilters()"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-x h-3 w-3"
                            aria-hidden="true"
                        >
                            <path d="M18 6 6 18"></path>
                            <path d="m6 6 12 12"></path></svg
                        >Clear All
                    </button>
                </div>
            </div>
            <div class="overflow-x-auto">
                <table class="w-full">
                    <thead class="border-b border-gray-100 bg-gray-50">
                        <tr>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                Emails
                            </th>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                From Domain
                            </th>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                SPF Result &amp; Alignment
                            </th>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                DKIM Result &amp; Alignment
                            </th>
                            <th
                                class="px-6 py-4 text-center text-sm font-medium text-gray-600"
                            >
                                DMARC Status
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr
                            v-for="(row, eventKey) in filteredResults"
                            :key="eventKey"
                            class="transition-colors duration-150 hover:bg-gray-50"
                        >
                            <td class="px-6 py-4">
                                <span class="font-medium text-gray-900">{{
                                    new Intl.NumberFormat().format(row.sum)
                                }}</span>
                            </td>

                            <!--SPF-->
                            <td class="px-6 py-4">
                                <button
                                    class="group/domain -m-2 flex cursor-pointer items-center gap-3 rounded-lg p-2 text-left transition-colors duration-150 hover:bg-blue-50"
                                    @click="showDetails(row)"
                                >
                                    <span
                                        class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 group-hover/domain:underline"
                                        >{{ row.identifiers.header_from }}</span
                                    >
                                </button>
                            </td>
                            <td class="px-6 py-4">
                                <div class="space-y-2">
                                    <!--SPF PASS-->
                                    <div
                                        v-if="
                                            row.auth_results.spf.result ===
                                            'pass'
                                        "
                                        class="mr-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big h-3 w-3"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <span class="capitalize">{{
                                            $t("vue.pass")
                                        }}</span>
                                    </div>
                                    <div
                                        v-else
                                        class="mr-2 inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-alert h-3 w-3"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <line
                                                x1="12"
                                                x2="12"
                                                y1="8"
                                                y2="12"
                                            ></line>
                                            <line
                                                x1="12"
                                                x2="12.01"
                                                y1="16"
                                                y2="16"
                                            ></line>
                                        </svg>
                                        <span class="capitalize">Fail</span>
                                    </div>

                                    <!--SPF ALIGN-->
                                    <div
                                        v-if="
                                            row.auth_results.spf.domain ===
                                            row.identifiers.header_from
                                        "
                                        class="mr-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-alert h-3 w-3"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <line
                                                x1="12"
                                                x2="12"
                                                y1="8"
                                                y2="12"
                                            ></line>
                                            <line
                                                x1="12"
                                                x2="12.01"
                                                y1="16"
                                                y2="16"
                                            ></line>
                                        </svg>
                                        <span class="capitalize">aligned</span>
                                    </div>
                                    <div
                                        v-else
                                        class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-alert h-3 w-3"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <line
                                                x1="12"
                                                x2="12"
                                                y1="8"
                                                y2="12"
                                            ></line>
                                            <line
                                                x1="12"
                                                x2="12.01"
                                                y1="16"
                                                y2="16"
                                            ></line>
                                        </svg>
                                        <span class="capitalize"
                                            >not aligned</span
                                        >
                                    </div>

                                    <!--SPF DOMAIN-->
                                    <div
                                        v-if="
                                            Array.isArray(
                                                row.auth_results.spf,
                                            ) === true
                                        "
                                    >
                                        <div
                                            v-for="spfResult in row.auth_results
                                                .spf"
                                            :key="spfResult"
                                            class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                                        >
                                            {{ spfResult.domain }}
                                        </div>
                                    </div>

                                    <div
                                        v-else-if="
                                            row.auth_results.spf &&
                                            typeof row.auth_results.spf ===
                                                'object' &&
                                            row.auth_results.spf.domain !== ''
                                        "
                                        class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                                    >
                                        {{ row.auth_results.spf.domain }}
                                    </div>

                                    <div
                                        v-else
                                        class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                                    >
                                        spf domain name not available
                                    </div>
                                </div>
                            </td>

                            <!--DKIM-->
                            <td class="px-6 py-4">
                                <template
                                    v-if="
                                        row.auth_results.dkim !== undefined &&
                                        row.auth_results.dkim.constructor ===
                                            Array
                                    "
                                >
                                    <!--DKIM PASS-->
                                    <div
                                        v-for="record in row.auth_results.dkim"
                                        :key="record.domain"
                                        class="mb-1 space-y-2 mb-5"
                                    >
                                        <div
                                            v-if="record.result === 'pass'"
                                            class="mr-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-check-big h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                                ></path>
                                                <path d="m9 11 3 3L22 4"></path>
                                            </svg>
                                            <span class="capitalize">pass</span>
                                        </div>
                                        <div
                                            v-else-if="
                                                record.result === 'neutral'
                                            "
                                            class="mr-2 inline-flex items-center gap-1 rounded-full border border-yellow-200 bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-check-big h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                                ></path>
                                                <path d="m9 11 3 3L22 4"></path>
                                            </svg>
                                            <span class="capitalize"
                                                >neutral</span
                                            >
                                        </div>
                                        <div
                                            v-else
                                            class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 mr-2"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-alert h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                ></circle>
                                                <line
                                                    x1="12"
                                                    x2="12"
                                                    y1="8"
                                                    y2="12"
                                                ></line>
                                                <line
                                                    x1="12"
                                                    x2="12.01"
                                                    y1="16"
                                                    y2="16"
                                                ></line>
                                            </svg>
                                            <span class="capitalize">Fail</span>
                                        </div>

                                        <!--DKIM PASS-->
                                        <div
                                            v-if="
                                                record.domain ===
                                                row.identifiers.header_from
                                            "
                                            class="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-check-big h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                                ></path>
                                                <path d="m9 11 3 3L22 4"></path>
                                            </svg>
                                            <span class="capitalize"
                                                >aligned</span
                                            >
                                        </div>
                                        <div
                                            v-else
                                            class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-alert h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                ></circle>
                                                <line
                                                    x1="12"
                                                    x2="12"
                                                    y1="8"
                                                    y2="12"
                                                ></line>
                                                <line
                                                    x1="12"
                                                    x2="12.01"
                                                    y1="16"
                                                    y2="16"
                                                ></line>
                                            </svg>
                                            <span class="capitalize"
                                                >not aligned</span
                                            >
                                        </div>
                                        <!--DKIM DOMAIN-->
                                        <div
                                            class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500"
                                        >
                                            {{ record.domain }}
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="mb-1 space-y-2 mb-5">
                                        <div
                                            v-if="
                                                row.auth_results.dkim &&
                                                row.auth_results.dkim.result ===
                                                    'pass'
                                            "
                                            class="mr-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-check-big h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                                ></path>
                                                <path d="m9 11 3 3L22 4"></path>
                                            </svg>
                                            <span class="capitalize">pass</span>
                                        </div>
                                        <div
                                            v-else
                                            class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 mr-2"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-alert h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                ></circle>
                                                <line
                                                    x1="12"
                                                    x2="12"
                                                    y1="8"
                                                    y2="12"
                                                ></line>
                                                <line
                                                    x1="12"
                                                    x2="12.01"
                                                    y1="16"
                                                    y2="16"
                                                ></line>
                                            </svg>
                                            <span class="capitalize">Fail</span>
                                        </div>

                                        <div
                                            v-if="
                                                row.auth_results.dkim &&
                                                row.auth_results.dkim.domain ===
                                                    row.identifiers.header_from
                                            "
                                            class="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-check-big h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                                ></path>
                                                <path d="m9 11 3 3L22 4"></path>
                                            </svg>
                                            <span class="capitalize"
                                                >aligned</span
                                            >
                                        </div>
                                        <div
                                            v-else
                                            class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-circle-alert h-3 w-3"
                                                aria-hidden="true"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                ></circle>
                                                <line
                                                    x1="12"
                                                    x2="12"
                                                    y1="8"
                                                    y2="12"
                                                ></line>
                                                <line
                                                    x1="12"
                                                    x2="12.01"
                                                    y1="16"
                                                    y2="16"
                                                ></line>
                                            </svg>
                                            <span class="capitalize"
                                                >not aligned</span
                                            >
                                        </div>

                                        <div
                                            v-if="
                                                row.auth_results.dkim !==
                                                    undefined &&
                                                row.auth_results.dkim.domain
                                            "
                                            class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                                        >
                                            {{ row.auth_results.dkim.domain }}
                                        </div>
                                        <div
                                            v-else
                                            class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                                        >
                                            dkim domain name not available
                                        </div>
                                    </div>
                                </template>
                            </td>

                            <td class="px-6 py-4 text-center">
                                <svg
                                    v-if="row.fail > 0"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-x mx-auto h-4 w-4 text-red-500"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path d="m15 9-6 6"></path>
                                    <path d="m9 9 6 6"></path>
                                </svg>
                                <svg
                                    v-else
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-check-big mx-auto h-4 w-4 text-green-500"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M21.801 10A10 10 0 1 1 17 3.335"
                                    ></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <DetailedModal
        v-if="showDetail"
        :detail-row="detailRow"
        :ip="ip"
        :reported-domain="reportedDomain"
        @close="hideDetailModal"
    />
</template>

<script lang="ts" setup>
import type ISelectable from "@/types/Atomic/ISelectable";
import type { PropType } from "vue";

const filters = ref([]);

const props = defineProps({
    reports: {
        type: Array as PropType<ISelectable[]>,
        default: () => [],
    },
    source: {
        type: String,
        default: () => "",
    },
    ip: {
        type: String,
        default: () => "",
    },
    reportedDomain: {
        type: String,
        default: () => "",
    },
});

const passRates = computed(() => {
    let spfValid = 0;
    let dkimValid = 0;
    let dmarcValid = 0;
    let total = 0;

    for (const row of Object.values(props.reports.events)) {
        spfValid += row.spf_valid ? row.sum : 0;
        dkimValid += row.dkim_valid ? row.sum : 0;
        dmarcValid += row.fail === 0 ? row.sum : 0;
        total += row.sum;
    }

    return {
        spf: total ? Math.floor((spfValid / total) * 1000) / 10 : 0,
        dkim: total ? Math.floor((dkimValid / total) * 1000) / 10 : 0,
        dmarc: total ? Math.floor((dmarcValid / total) * 1000) / 10 : 0,
    };
});

const peakDay = computed(() => {
    return props.reports.chartData.reduce((peak, current) => {
        const currentTotal = current.pass + current.fail;
        const peakTotal = peak.pass + peak.fail;
        return currentTotal > peakTotal ? current : peak;
    });
});

function hideDetailModal() {
    showDetail.value = false;
}

interface RowData {
    source_ip: string;
    count: number;
    policy_evaluated: object;
    identifiers: object;
    auth_results: object;
}

const detailRow = ref<RowData | null>(null);
const showDetail = ref(false);

const showDetails = (row: RowData) => {
    detailRow.value = row;
    showDetail.value = true;
};

const filteredResults = computed(() => {
    let events = props.reports.events;

    if (filters.value.includes("dmarc")) {
        events = Object.entries(events).reduce((acc, [key, event]) => {
            if (event.fail > 0) {
                acc[key] = event;
            }
            return acc;
        }, {});
    }

    if (filters.value.includes("spf")) {
        events = Object.entries(events).reduce((acc, [key, event]) => {
            if (event.spf_invalid > 0) {
                acc[key] = event;
            }
            return acc;
        }, {});
    }

    if (filters.value.includes("dkim")) {
        events = Object.entries(events).reduce((acc, [key, event]) => {
            if (event.dkim_invalid > 0) {
                acc[key] = event;
            }
            return acc;
        }, {});
    }

    return events;
});

const firstInvalidSpf = computed(() => {
    return Object.entries(props.reports.events).find(
        ([_, event]) => event.spf_invalid > 0,
    );
});

const addFilter = (filter: string) => {
    if (filters.value.includes(filter)) {
        filters.value = filters.value.filter((e) => e !== filter);
    } else {
        filters.value.push(filter);
    }
};

const generateDmarcSummary = (passRates) => {
    let headline;

    if (
        passRates.spf === 100 &&
        passRates.dkim === 100 &&
        passRates.dmarc >= 99
    ) {
        headline = "Excellent Performance";
    } else if (passRates.dmarc >= 95) {
        headline = "Good Performance";
    } else if (passRates.dmarc >= 80) {
        headline = "Moderate Performance";
    } else {
        headline = "Needs Attention";
    }

    const formatted = (value) => value.toFixed(1).replace(/\.0$/, "") + "%";

    return {
        headline: headline,
        summary: `${formatted(passRates.spf)} SPF and ${formatted(
            passRates.dkim,
        )} DKIM pass rates with ${formatted(
            passRates.dmarc,
        )} DMARC compliance.`,
    };
};

const clearFilters = () => {
    filters.value = [];
};

const calculateColorByPercents = (percents) => {
    if (percents > 75) {
        return "#15803D";
    }

    if (percents > 50) {
        return "#CA8A04";
    }

    return "#B91C1C";
};
</script>
